import React, { useRef, forwardRef, useEffect, useState } from "react";
// We neeed to use this deprecated module for now until expo fixes release channels
import { Keyboard, Platform, Pressable, StyleSheet, View, ViewStyle } from "react-native";

import { Picker } from "@react-native-picker/picker";
import { ActivityIndicator, Modal, Portal, Text, useTheme } from "react-native-paper";
import { placeholder } from "i18n-js";
import { ScrollView, TouchableHighlight } from "react-native-gesture-handler";

import { DropDownChoice } from "../../../types/components/dropdown";
import { TextInput } from "../../components";
import { ThemeType } from "../../theme/theme";
import { DropdownType } from "../../enums/dropdown";
import startCase from "lodash/startCase";
import { testProps } from "../../utils/testProps";
import { TextInput as TextInputUiComponents } from "ui-components";

const DEFAULT_VALUE = "default";

export interface IDropdownProps extends Record<string, any> {
  onChangeText?: Function;
  onSelectHandler: Function;
  value?: any;
  isLoading?: boolean;
  items: DropDownChoice[];
  mode?: DropdownType;
  disallowFocus?: any;
  iconName?: string;
  activeIconName?: string;
  label?: string;
  placeholder?: any;
  formName: string;
  disabled?: boolean;
  errors?: any;
  inputAccessoryViewID?: string;
  onFocus?: any;
  onSubmitEditing?: any;
  useUiComponentTextInput?: boolean;
  textInputLabel?: string;
  dropdownSelectorContainerStyle?: ViewStyle;
  useTouchableHighlightFromReactNative?: boolean;
}

export const Dropdown = forwardRef((props: IDropdownProps, ref: any) => {
  const {
    formName,
    onChangeText,
    onSelectHandler,
    onFocus,
    disallowFocus = false,
    isLoading,
    mode = DropdownType.DIALOG,
    inputAccessoryViewID,
    onSubmitEditing,
    value,
    items,
    iconName,
    activeIconName,
    disabled,
    label,
    errors,
    placeholder,
    useUiComponentTextInput = false,
    textInputLabel,
    dropdownSelectorContainerStyle,
    useTouchableHighlightFromReactNative = false,
  } = props;
  const theme = useTheme() as ThemeType;
  const { colors, spacing } = theme;
  const [isPickerVisible, setPickerVisible] = useState(false);
  const isDialogMode = mode === DropdownType.DIALOG;
  const styles = getStyles({ theme, disabled });
  const selectValueLabel = value ? value.label || value : undefined;
  const textInput = useRef();

  const DropDownTouchableComponent = useTouchableHighlightFromReactNative
    ? Pressable
    : TouchableHighlight;

  useEffect(() => {
    if (!selectValueLabel && textInput.current) {
      textInput.current.clear();
    }
  }, [selectValueLabel]);

  const inputIconName = iconName
    ? activeIconName
      ? isPickerVisible
        ? activeIconName
        : iconName
      : iconName
    : undefined;

  const onItemSelectHandler = (value: string) => {
    if (value === DEFAULT_VALUE) {
      onSelectHandler(undefined);
      return;
    }
    onSelectHandler(value);
    setPickerVisible(false);
    Keyboard.dismiss();
  };

  const onTextChangeHandler = (text: any) => {
    if (isDialogMode) return;
    setPickerVisible(true);
    onChangeText && onChangeText(text);
    onSubmitEditing && onSubmitEditing();
  };

  const onFocusHandler = () => {
    if (disabled) return;
    if (!disallowFocus && textInput.current) textInput.current.focus();
    onFocus && onFocus();
    setPickerVisible(true);
    if (!isDialogMode) return;
    Keyboard.dismiss();
  };

  const onPressHandler = () => {
    if (disabled) return;
    if (isPickerVisible) {
      setPickerVisible(false);
    } else {
      onFocusHandler && onFocusHandler();
    }
  };

  const DialogSelector = () => {
    if ((!isPickerVisible || !items?.length || disabled) && !isLoading) return null;
    return (
      <Portal>
        <Modal
          visible={isPickerVisible}
          onDismiss={() => setPickerVisible(false)}
          contentContainerStyle={styles.dialogSelector}>
          <Picker style={styles.select} selectedValue={value} onValueChange={onItemSelectHandler}>
            <Picker.Item value={DEFAULT_VALUE} label={label || ""} />
            {items.map((item: any, index: number) => {
              const itemLabel =
                typeof item === "string" ? item : item?.label || startCase(item?.value);
              return (
                <Picker.Item value={item?.value || item || ""} key={index} label={itemLabel} />
              );
            })}
          </Picker>
        </Modal>
      </Portal>
    );
  };

  const DropdownSelector = () => {
    if ((!isPickerVisible || !items?.length || disabled) && !isLoading) return null;
    return (
      <View style={[styles.dropdownSelector, dropdownSelectorContainerStyle]}>
        <ScrollView keyboardShouldPersistTaps={"always"}>
          {isLoading ? (
            <ActivityIndicator style={{ paddingVertical: spacing.small }} />
          ) : (
            items.map((item: any, index: any) => (
              <DropDownTouchableComponent
                key={index}
                activeOpacity={0.5}
                underlayColor={colors.darkBg}
                onPress={() => onItemSelectHandler(item)}>
                <Text style={styles.dropdownItem}>{item.label || item}</Text>
              </DropDownTouchableComponent>
            ))
          )}
        </ScrollView>
      </View>
    );
  };

  if (Platform.OS === "android" && isDialogMode) {
    const hasItems = Boolean(items && items.length && !isLoading && !disabled);
    return (
      <View style={styles.androidPicker}>
        <Picker
          selectedValue={value}
          enabled={hasItems}
          style={styles.picker}
          onValueChange={onItemSelectHandler}>
          <Picker.Item value={DEFAULT_VALUE} label={label || placeholder || " "} />
          {hasItems &&
            items.map((item: any, index: number) => {
              const itemLabel =
                typeof item === "string" ? item : item?.label || startCase(item?.value);
              return <Picker.Item value={item.value || item} key={index} label={itemLabel} />;
            })}
        </Picker>
      </View>
    );
  } else {
    return (
      <>
        <Pressable style={{ outlineStyle: "none" } as any} onPress={onPressHandler}>
          <View pointerEvents="none" accessible={true}>
            {!useUiComponentTextInput ? (
              <TextInput
                ref={textInput}
                value={selectValueLabel}
                error={errors?.[formName]}
                mode="outlined"
                disabled={disabled}
                iconName={inputIconName}
                onFocus={onFocusHandler}
                onChangeText={onTextChangeHandler}
                inputAccessoryViewID={inputAccessoryViewID}
                placeholder={placeholder}
                onSubmitEditing={onSubmitEditing}
                {...testProps(formName)}
              />
            ) : (
              <TextInputUiComponents
                ref={textInput}
                label={textInputLabel}
                value={selectValueLabel}
                error={errors?.[formName]}
                disabled={disabled}
                iconName={inputIconName}
                onFocus={onFocusHandler}
                onChangeText={onTextChangeHandler}
                inputAccessoryViewID={inputAccessoryViewID}
                placeholder={placeholder}
                onSubmitEditing={onSubmitEditing}
                {...testProps(formName)}
              />
            )}
          </View>
        </Pressable>
        {isDialogMode ? <DialogSelector /> : <DropdownSelector />}
      </>
    );
  }
});

const getStyles = ({ theme, disabled }: any) => {
  const { colors, spacing } = theme;
  return StyleSheet.create({
    dialogSelector: {
      display: "flex",
      marginHorizontal: spacing.medium,
      backgroundColor: colors.background,
      borderRadius: 10,
      flexDirection: "column",
    },
    dropdownSelector: {
      display: "flex",
      position: "absolute",
      width: "100%",
      borderWidth: 1,
      borderColor: colors.darkBg,
      padding: 0,
      margin: 0,
      maxHeight: 350,
      top: "96%",
      backgroundColor: colors.white,
      shadowOffset: {
        width: 0,
        height: 5,
      },
      shadowOpacity: 0.4,
      shadowRadius: spacing.tiny,
      zIndex: 10,
      elevation: 2,
    },
    dropdownItem: {
      marginHorizontal: spacing.smaller,
      marginVertical: spacing.small,
    },
    androidPicker: {
      height: 58,
      borderRadius: spacing.tiny,
      borderColor: colors.darkGrey,
      borderWidth: 1,
      borderStyle: "solid",
      backgroundColor: disabled ? colors.lightGrey : colors.white,
    },
    picker: {
      flex: 1,
      color: disabled ? colors.darkGrey : colors.text,
    },
    select: {
      padding: 15,
    },
  });
};
